(function() {
    'use strict';
    
    console.log('Discount manager script loaded');
    
    // Discount Manager - integrates with existing CartUpdater
    const DiscountManager = {
        appliedDiscounts: new Set(),
        discountInput: null,
        discountButton: null,
        couponWrapper: null,
        
        // Initialize discount functionality
        init() {
            console.log('Initializing discount manager...');
            this.findDiscountElements();
            this.bindEvents();
            this.loadExistingDiscounts();
            this.integrateWithCartUpdater();
        },
        
        // Find existing discount input elements
        findDiscountElements() {
            // Use your specific elements
            this.discountInput = document.querySelector('#coupon-code-input');
            this.discountButton = document.querySelector('#apply-coupon-btn');
            this.couponWrapper = document.querySelector('.coupon-input-wrapper');
            
            console.log('Discount elements found:', {
                input: !!this.discountInput,
                button: !!this.discountButton,
                wrapper: !!this.couponWrapper
            });
        },
        
        // Bind event listeners
        bindEvents() {
            // Apply discount button
            if (this.discountButton) {
                this.discountButton.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.applyDiscount();
                });
            }
            
            // Enter key on input
            if (this.discountInput) {
                this.discountInput.addEventListener('keypress', (e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        this.applyDiscount();
                    }
                });
                
                // Clear error on input
                this.discountInput.addEventListener('input', () => {
                    this.hideError();
                });
            }
            
            // Remove discount buttons (event delegation)
            document.addEventListener('click', (e) => {
                if (e.target.matches('.coupon-remove-btn')) {
                    e.preventDefault();
                    const discountCode = e.target.dataset.discountCode;
                    if (discountCode) {
                        this.removeDiscount(discountCode);
                    }
                }
            });
        },
        
        // Apply discount code using Shopify Summer '25 cart/update.js endpoint
        async applyDiscount() {
            if (!this.discountInput) {
                console.error('Discount input not found');
                return;
            }
            
            const discountCode = this.discountInput.value.trim();
            
            if (!discountCode) {
                this.showError('Please enter a discount code');
                return;
            }
            
            if (this.appliedDiscounts.has(discountCode.toUpperCase())) {
                this.showError('This discount code is already applied');
                return;
            }
            
            this.showLoading(true);
            console.log('Applying discount via cart/update.js:', discountCode);
            
            try {
                // Use Shopify Summer '25 cart/update.js endpoint with discount parameter
                const localeAwareUrl = (window.Shopify?.routes?.root || '') + 'cart/update.js';
                const response = await fetch(localeAwareUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                    },
                    body: JSON.stringify({
                        discount: discountCode
                    })
                });
                
                if (response.ok) {
                    console.log('Discount update response received');
                    
                    // Verify the discount was actually applied by checking cart
                    await this.verifyDiscountApplied(discountCode);
                } else {
                    const errorData = await response.text();
                    console.log('Discount application failed:', response.status, errorData);
                    this.showError('Unable to apply discount code. Please check if it\'s valid.');
                }
            } catch (error) {
                console.error('Error applying discount:', error);
                this.showError('Network error. Please try again.');
            } finally {
                this.showLoading(false);
            }
        },
        
        // Verify discount was applied by checking cart data
        async verifyDiscountApplied(discountCode) {
            try {
                const localeAwareUrl = (window.Shopify?.routes?.root || '') + 'cart.js';
                const cartResponse = await fetch(localeAwareUrl);
                const cart = await cartResponse.json();
                
                console.log('Cart verification response:', cart);
                
                // Check if discount was applied
                const wasApplied = this.checkForDiscount(cart, discountCode);
                
                if (wasApplied) {
                    console.log('Discount successfully applied and verified');
                    
                    // Add to applied discounts
                    this.appliedDiscounts.add(discountCode.toUpperCase());
                    
                    // Clear input and transform to applied state
                    this.discountInput.value = '';
                    this.showAppliedCoupon(discountCode);
                    
                    // Trigger cart update using existing CartUpdater
                    this.triggerCartUpdate();
                    
                    this.hideError();
                    this.showSuccess(`Discount code "${discountCode}" applied successfully!`);
                    
                    // Reload page to show updated totals
                    setTimeout(() => {
                        window.location.reload();
                    }, 1000);
                } else {
                    console.log('Discount was not applied to cart');
                    this.showError('Discount code could not be applied. Please check if it\'s valid and meets requirements.');
                }
            } catch (error) {
                console.error('Error verifying discount:', error);
                this.showError('Error verifying discount application.');
            }
        },
        
        // Check if discount is applied in cart data
        checkForDiscount(cart, code) {
            // Check for cart-level discount applications
            if (cart.cart_level_discount_applications && 
                cart.cart_level_discount_applications.some(app => 
                    app.title?.toLowerCase() === code.toLowerCase()
                )) {
                return true;
            }
            
            // Check for line item discounts
            if (cart.items && cart.items.some(item => 
                item.discounts?.some(discount => 
                    discount.title?.toLowerCase() === code.toLowerCase()
                )
            )) {
                return true;
            }
            
            // Check for discount codes array (newer format)
            if (cart.discount_codes && 
                cart.discount_codes.some(discount => 
                    discount.code?.toLowerCase() === code.toLowerCase()
                )) {
                return true;
            }
            
            return false;
        },
        
        // Remove discount code
        async removeDiscount(discountCode) {
            this.showLoading(true);
            console.log('Removing discount via cart/update.js:', discountCode);
            
            try {
                // Remove discount by setting empty discount
                const localeAwareUrl = (window.Shopify?.routes?.root || '') + 'cart/update.js';
                const response = await fetch(localeAwareUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                    },
                    body: JSON.stringify({
                        discount: ''
                    })
                });
                
                if (response.ok) {
                    console.log('Discount removal response received');
                    
                    // Verify the discount was removed
                    const localeAwareCartUrl = (window.Shopify?.routes?.root || '') + 'cart.js';
                    const cartResponse = await fetch(localeAwareCartUrl);
                    const cart = await cartResponse.json();
                    
                    const wasRemoved = !this.checkForDiscount(cart, discountCode);
                    
                    if (wasRemoved) {
                        console.log('Discount successfully removed from cart');
                        
                        // Remove from applied discounts
                        this.appliedDiscounts.delete(discountCode.toUpperCase());
                        
                        // Transform back to input form
                        this.hideAppliedCoupon();
                        
                        // Trigger cart update using existing CartUpdater
                        this.triggerCartUpdate();
                        
                        this.showSuccess(`Discount code "${discountCode}" removed successfully!`);
                        
                        // Reload page to show updated totals
                        setTimeout(() => {
                            window.location.reload();
                        }, 1000);
                    } else {
                        console.log('Discount was not removed from cart');
                        this.showError('Unable to remove discount code');
                    }
                } else {
                    console.log('Discount removal failed:', response.status);
                    this.showError('Unable to remove discount code');
                }
            } catch (error) {
                console.error('Error removing discount:', error);
                this.showError('Network error. Please try again.');
            } finally {
                this.showLoading(false);
            }
        },
        
        // Trigger cart update using existing CartUpdater
        triggerCartUpdate() {
            // Use existing CartUpdater if available
            if (window.CartUpdater) {
                console.log('Triggering CartUpdater.updateCartElements()');
                setTimeout(() => {
                    window.CartUpdater.updateCartElements();
                }, 300);
            }
            
            // Also dispatch custom events that existing cart might listen to
            const events = ['cart:updated', 'cart:changed', 'cartUpdated'];
            events.forEach(eventName => {
                document.dispatchEvent(new CustomEvent(eventName, {
                    detail: { source: 'discount-manager' }
                }));
            });
        },
        
        // Show applied coupon by transforming the existing wrapper
        showAppliedCoupon(discountCode) {
            if (!this.couponWrapper) return;
            
            // Transform the wrapper to show applied coupon
            this.couponWrapper.innerHTML = `
                <div class="applied-coupon-info" style="display: flex; align-items: center; gap: 8px;">
                    <span class="applied-coupon-code" style="font-weight: 500;">${discountCode.toUpperCase()}</span>
                    <svg class="coupon-check-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" style="color: green;">
                        <path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                    </svg>
                </div>
                <button type="button" class="coupon-remove-btn" data-discount-code="${discountCode.toLowerCase()}" aria-label="Remove coupon code" style="background: none; border: 1px solid #ccc; padding: 4px 8px; cursor: pointer; border-radius: 4px;">
                    REMOVE
                </button>
            `;
            
            console.log('Transformed wrapper to show applied coupon');
        },
        
        // Hide applied coupon and restore input form
        hideAppliedCoupon() {
            if (!this.couponWrapper) return;
            
            // Restore original input form
            this.couponWrapper.innerHTML = `
                <input type="text" id="coupon-code-input" class="coupon-input" placeholder="Enter Coupon code" autocomplete="off">
                <button type="button" id="apply-coupon-btn" class="coupon-apply-btn" aria-label="Apply coupon code" style="display: inline-block;">
                    APPLY
                </button>
            `;
            
            // Update references to new elements
            this.discountInput = document.querySelector('#coupon-code-input');
            this.discountButton = document.querySelector('#apply-coupon-btn');
            
            // Re-bind events for new elements
            this.bindEvents();
            
            console.log('Restored input form');
        },
        
        // Load existing discounts from DOM
        loadExistingDiscounts() {
            // Check for existing applied discounts in the DOM
            const existingCoupons = document.querySelectorAll('.applied-coupon-code');
            existingCoupons.forEach(coupon => {
                const code = coupon.textContent.trim().toUpperCase();
                this.appliedDiscounts.add(code);
                console.log('Found existing discount:', code);
            });
            
            // If there are applied discounts, show applied state
            if (this.appliedDiscounts.size > 0) {
                // Already in applied state, do nothing
                console.log('Discount already applied, maintaining state');
            } else {
                // Make sure we're in input state
                this.hideAppliedCoupon(); // This will restore the input form
            }
        },
        
        // Integrate with existing CartUpdater
        integrateWithCartUpdater() {
            // Wait for CartUpdater to be available
            const checkCartUpdater = () => {
                if (window.CartUpdater) {
                    console.log('CartUpdater found, integrating discount functionality');
                    
                    // Extend CartUpdater's updateDiscount method to work with our discount display
                    const originalUpdateDiscount = window.CartUpdater.updateDiscount;
                    if (originalUpdateDiscount) {
                        window.CartUpdater.updateDiscount = (cartData) => {
                            // Call original method
                            originalUpdateDiscount.call(window.CartUpdater, cartData);
                            
                            // Update our discount display state
                            this.syncDiscountState(cartData);
                        };
                    }
                } else {
                    // Retry after 100ms
                    setTimeout(checkCartUpdater, 100);
                }
            };
            
            checkCartUpdater();
        },
        
        // Sync discount state with cart data
        syncDiscountState(cartData) {
            const originalTotal = cartData.original_total_price || cartData.total_price || 0;
            const finalTotal = cartData.total_price || 0;
            const hasDiscount = originalTotal > finalTotal;
            
            // Check if cart data has discount codes
            const cartDiscountCodes = cartData.discount_codes || [];
            
            console.log('Syncing discount state:', { 
                hasDiscount, 
                cartDiscountCodes,
                appliedDiscounts: this.appliedDiscounts.size 
            });
            
            // Update our applied discounts based on cart data
            if (cartDiscountCodes.length > 0) {
                this.appliedDiscounts.clear();
                cartDiscountCodes.forEach(discount => {
                    this.appliedDiscounts.add(discount.code.toUpperCase());
                });
                
                // Show applied coupon with the first discount code
                this.showAppliedCoupon(cartDiscountCodes[0].code);
            } else {
                // No discount codes in cart
                this.appliedDiscounts.clear();
                this.hideAppliedCoupon(); // This will restore input form
            }
        },
        
        // Show loading state
        showLoading(show) {
            if (this.discountButton) {
                if (show) {
                    this.discountButton.disabled = true;
                    this.discountButton.textContent = 'Applying...';
                } else {
                    this.discountButton.disabled = false;
                    this.discountButton.textContent = 'APPLY';
                }
            }
        },
        
        // Show error message
        showError(message) {
            // Create or update error element near input
            let errorElement = document.querySelector('.discount-error');
            
            if (!errorElement && this.couponWrapper) {
                const errorHTML = `<div class="discount-error" style="color: #d72c0d; font-size: 14px; margin-top: 5px;"></div>`;
                this.couponWrapper.insertAdjacentHTML('afterend', errorHTML);
                errorElement = document.querySelector('.discount-error');
            }
            
            if (errorElement) {
                errorElement.textContent = message;
                errorElement.style.display = 'block';
                
                // Auto-hide error after 5 seconds
                setTimeout(() => {
                    this.hideError();
                }, 5000);
            } else {
                // Fallback: log to console
                console.error('Discount Error:', message);
            }
        },
        
        // Hide error message
        hideError() {
            const errorElement = document.querySelector('.discount-error');
            if (errorElement) {
                errorElement.style.display = 'none';
            }
        },
        
        // Show success message
        showSuccess(message) {
            const errorElement = document.querySelector('.discount-error');
            if (errorElement) {
                errorElement.textContent = message;
                errorElement.style.display = 'block';
                errorElement.style.color = '#008060';
                
                // Hide success message after 3 seconds
                setTimeout(() => {
                    this.hideError();
                }, 3000);
            }
        },
        
        // Public method to manually refresh discounts
        refresh() {
            console.log('Refreshing discount state');
            this.loadExistingDiscounts();
            this.triggerCartUpdate();
        }
    };
    
    // Initialize discount manager when DOM is ready
    const initDiscountManager = () => {
        // Wait a bit for CartUpdater to initialize first
        setTimeout(() => {
            DiscountManager.init();
        }, 500);
    };
    
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initDiscountManager);
    } else {
        initDiscountManager();
    }
    
    // Backup on window load
    window.addEventListener('load', initDiscountManager);
    
    // Expose for debugging
    window.DiscountManager = DiscountManager;
    
})();